/**
 * PartBundlePicker — "Add bundle" for the parts of a work-order / quote service (#3350 phase 1).
 *
 * One selection reads the bundle's expansion at today's catalog prices and hands every
 * line to the caller, which appends them to its own draft as ordinary part rows. This
 * component saves nothing and touches no stock: the caller's normal autosave (with its
 * version token and conflict handling) is the only write path. If any part of the bundle
 * is no longer available the whole selection is refused and nothing is inserted.
 *
 * All strings live in the `inventory` namespace (`bundles.picker.*`).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotify } from '@/hooks/useNotify';
import {
  fetchPartBundleExpansion,
  usePartBundleOptions,
  type PartBundleExpansion,
  type PartBundleListItem,
} from '@/hooks/useInventory';
import { describeApiError } from '@/lib/describeApiError';
import { useAuthStore } from '@/stores/authStore';

export interface PartBundlePickerProps {
  /** Receives every line of the chosen bundle at once; it must append them as ordinary part rows. */
  onInsert: (expansion: PartBundleExpansion) => void;
  disabled?: boolean;
}

interface UnavailablePartsBody {
  unavailableParts?: { name?: unknown }[];
}

/** Names of the parts the server refused to expand, or null when the error is something else. */
function unavailablePartNames(error: unknown): string[] | null {
  const status = (error as { status?: unknown } | null)?.status;
  const data = (error as { data?: UnavailablePartsBody } | null)?.data;
  if (status !== 409 || !Array.isArray(data?.unavailableParts)) return null;
  return data.unavailableParts.map((part) => (typeof part.name === 'string' ? part.name : ''));
}

export function PartBundlePicker({ onInsert, disabled = false }: PartBundlePickerProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const canReadInventory = useAuthStore((state) => state.hasPermission('inventory.read'));

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [insertingId, setInsertingId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const query = useDebounce(search.trim(), 250);
  const bundles = usePartBundleOptions(query ? { q: query } : {}, { enabled: open && canReadInventory });

  if (!canReadInventory) return null;

  const close = () => {
    if (insertingId) return;
    setOpen(false);
    setSearch('');
    setRefusal(null);
  };

  const choose = async (bundle: PartBundleListItem) => {
    if (insertingId) return;
    setInsertingId(bundle.id);
    setRefusal(null);
    try {
      const expansion = await fetchPartBundleExpansion(bundle.id);
      if (!mountedRef.current) return;
      onInsert(expansion);
      notify.success(
        t('bundles.picker.inserted'),
        t('bundles.picker.insertedDesc', { count: expansion.lines.length, name: expansion.bundleName }),
      );
      setOpen(false);
      setSearch('');
    } catch (error) {
      if (!mountedRef.current) return;
      const names = unavailablePartNames(error);
      const message =
        names !== null
          ? t('bundles.picker.unavailable', { name: bundle.name, parts: names.join(', ') })
          : describeApiError(error, tc, { fallback: t('bundles.picker.failed') });
      setRefusal(message);
      notify.error(tc('error'), message);
    } finally {
      if (mountedRef.current) setInsertingId(null);
    }
  };

  const rows = bundles.data?.data ?? [];

  return (
    <>
      <Tooltip title={t('bundles.picker.button')}>
        <span>
          <Button
            size="small"
            startIcon={<InventoryIcon fontSize="small" />}
            endIcon={<AddIcon fontSize="small" />}
            onClick={() => setOpen(true)}
            disabled={disabled}
            aria-label={t('bundles.picker.button')}
          >
            {t('bundles.picker.button')}
          </Button>
        </span>
      </Tooltip>

      <ResponsiveDialog
        open={open}
        onClose={close}
        maxWidth="xs"
        fullWidth
        mobileTitle={t('bundles.picker.title')}
        aria-labelledby="part-bundle-picker-title"
      >
        <DialogTitle id="part-bundle-picker-title">{t('bundles.picker.title')}</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t('bundles.picker.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ mb: 1, mt: 0.5 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': t('bundles.picker.searchPlaceholder') },
            }}
          />
          {refusal && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {refusal}
            </Alert>
          )}
          {bundles.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} aria-label={tc('loading')} />
            </Box>
          ) : bundles.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void bundles.refetch()}>
                  {t('bundles.picker.retry')}
                </Button>
              }
            >
              {t('bundles.picker.loadFailed')}
            </Alert>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
              {t('bundles.picker.empty')}
            </Typography>
          ) : (
            <List dense disablePadding aria-label={t('bundles.picker.title')}>
              {rows.map((bundle) => (
                <ListItemButton
                  key={bundle.id}
                  onClick={() => void choose(bundle)}
                  disabled={insertingId !== null}
                  aria-label={bundle.name}
                >
                  <ListItemText
                    primary={bundle.name}
                    secondary={
                      [
                        bundle.description,
                        t('bundles.picker.partCount', { count: bundle.itemCount }),
                        bundle.unavailableCount > 0 ? t('bundles.picker.unavailableBadge') : null,
                      ]
                        .filter((part): part is string => typeof part === 'string' && part !== '')
                        .join(' · ')
                    }
                  />
                  {insertingId === bundle.id && <CircularProgress size={18} aria-label={t('bundles.picker.inserting')} />}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={insertingId !== null}>
            {tc('close')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
